import { ProductFormActionTypes } from "../actions/productFormActions";
import { ProductFormState, ProductFormField } from "../types";
import * as actions from "../actions/productFormActions";
// Initial state structure for product form
const INITIAL_STATE: ProductFormState = {
  // Product basics
  name: { value: '', valid: false, error: null, touched: false },
  description: { value: '', valid: true, error: null, touched: false },
  scope: { value: '', valid: true, error: null, touched: false },

  // Application types - dynamic (no hardcoded keys)
  selectedApplicationTypeCodes: { value: [], valid: true, error: null },
  redirectUrlsByType: { value: {}, valid: true, error: null },
  redirectUrlsValid: { value: true, valid: true, error: null },
  
  // Visibility
  visibility: { value: 'team', valid: true, error: null },
  
  // Approval flow
  approvalFlow: { value: 'open', valid: true, error: null },
  
  // Form state
  isFormValid: false,
  isEditing: false,
  product: null
};

/**
 * Product form reducer
 * @param state - Current state
 * @param action - Redux action
 * @returns New state
 */
export default (state = INITIAL_STATE, action: ProductFormActionTypes): ProductFormState => {
  switch (action.type) {
    case actions.UPDATE_FORM_FIELD:
      return updateFormField(state, action);
    case actions.SET_FIELD_VALIDATION:
      return setFieldValidation(state, action);
    case actions.RESET_FORM:
      return resetForm(state, action);
    case actions.INITIALIZE_FORM:
      return initializeForm(state, action);
    case actions.SET_FORM_EDITING:
      return setFormEditing(state, action);
    case actions.UPDATE_FORM_VALIDATION:
      return updateFormValidation(state, action);
    default:
      return state;
  }
};

/**
 * Update a form field value and validation state
 * @param state 
 * @param action 
 * @returns Updated state
 */
const updateFormField = (state: ProductFormState, action: any): ProductFormState => {
  const { fieldName, value, valid, error } = action.payload;
  
  const currentField = state[fieldName as keyof ProductFormState] as ProductFormField;
  const updatedField: ProductFormField = {
    ...currentField,
    value,
    valid: valid !== undefined ? valid : currentField.valid,
    error: error !== undefined ? error : currentField.error,
    touched: true
  };
  
  const newState = {
    ...state,
    [fieldName]: updatedField
  };
  
  // Update form validity
  return {
    ...newState,
    isFormValid: calculateFormValidity(newState)
  };
};

/**
 * Set field validation without updating value
 * @param state 
 * @param action 
 * @returns Updated state
 */
const setFieldValidation = (state: ProductFormState, action: any): ProductFormState => {
  const { fieldName, valid, error } = action.payload;
  
  const currentField = state[fieldName as keyof ProductFormState] as ProductFormField;
  const updatedField: ProductFormField = {
    ...currentField,
    valid,
    error
  };
  
  const newState = {
    ...state,
    [fieldName]: updatedField
  };
  
  return {
    ...newState,
    isFormValid: calculateFormValidity(newState)
  };
};

/**
 * Reset form to initial state
 * @param state 
 * @param action 
 * @returns Reset state
 */
const resetForm = (state: ProductFormState, action: any): ProductFormState => {
  return INITIAL_STATE;
};

/**
 * Initialize form with product data (for editing)
 * Uses applicationTypes from API (type + redirectUrls per type); no hardcoded app type keys.
 */
const initializeForm = (state: ProductFormState, action: any): ProductFormState => {
  const { product, isEditing } = action.payload;

  if (!product) {
    return { ...INITIAL_STATE, isEditing: false };
  }

  const selectedCodes: string[] = [];
  const redirectUrlsByType: Record<string, string[]> = {};

  if (product.applicationTypes && Array.isArray(product.applicationTypes) && product.applicationTypes.length > 0) {
    product.applicationTypes.forEach((item: { type?: string; redirectUrls?: string[] }) => {
      if (item?.type) {
        selectedCodes.push(item.type);
        if (item.redirectUrls && item.redirectUrls.length > 0) {
          redirectUrlsByType[item.type] = [...item.redirectUrls];
        }
      }
    });
  }

  return {
    ...state,
    name: {
      value: product.displayName || product.name || '',
      valid: !!(product.displayName || product.name),
      error: null,
      touched: false
    },
    description: {
      value: product.longDescription || '',
      valid: true,
      error: null,
      touched: false
    },
    scope: {
      value: product.scope || '',
      valid: true,
      error: null,
      touched: false
    },
    visibility: {
      value: product.visibility || 'team',
      valid: true,
      error: null
    },
    approvalFlow: {
      value: getApprovalFlowFromApi(product.publishedInfo?.[0]?.approvalType) || 'open',
      valid: true,
      error: null
    },
    selectedApplicationTypeCodes: { value: selectedCodes, valid: true, error: null },
    redirectUrlsByType: { value: redirectUrlsByType, valid: true, error: null },
    redirectUrlsValid: { value: true, valid: true, error: null },
    isEditing,
    product,
    isFormValid: !!(product.displayName || product.name)
  };
};

/**
 * Set form editing mode
 * @param state 
 * @param action 
 * @returns Updated state
 */
const setFormEditing = (state: ProductFormState, action: any): ProductFormState => {
  return {
    ...state,
    isEditing: action.payload.isEditing,
    product: action.payload.product || null
  };
};

/**
 * Update form validation state
 * @param state 
 * @param action 
 * @returns Updated state
 */
const updateFormValidation = (state: ProductFormState, action: any): ProductFormState => {
  return {
    ...state,
    isFormValid: calculateFormValidity(state)
  };
};

/**
 * Calculate overall form validity
 * @param state 
 * @returns Form validity
 */
const calculateFormValidity = (state: ProductFormState): boolean => {
  const isNameValid = state.isEditing 
    ? state.name.value.trim() !== '' 
    : (state.name.valid && state.name.value.trim() !== '');
  return isNameValid && state.description.valid && state.redirectUrlsValid.valid;
};

/**
 * Map API approval type to UI value
 * @param apiValue 
 * @returns UI value
 */
const getApprovalFlowFromApi = (apiValue: string): string => {
  const mapping: Record<string, string> = {
    'MANUAL': 'closed',
    'AUTO': 'open'
  };
  return mapping[apiValue] || 'open';
};